import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';
import { LOCATION_OPTIONS, SPACE_TYPE_OPTIONS, FEATURE_OPTIONS, NOISE_LEVELS } from '../../constants';

const FilterSection = ({ title, children }) => (
  <div className="mb-8">
    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">{title}</h3>
    {children}
  </div>
);

const FilterPanel = ({ isOpen, onClose, filters, setFilters }) => {
  const handleMultipleToggle = (field, value) => {
    setFilters(prev => {
      const currentValues = Array.isArray(prev[field]) ? prev[field] : [];
      return {
        ...prev,
        [field]: currentValues.includes(value)
          ? currentValues.filter(item => item !== value)
          : [...currentValues, value]
      };
    });
  };

  const Checkbox = ({ checked, onChange, label }) => (
    <label className="flex items-center gap-3 cursor-pointer group py-2">
      <div className={`
        relative flex items-center justify-center w-5 h-5 rounded border transition-all duration-200
        ${checked ? 'bg-purple-600 border-purple-600' : 'border-gray-600 group-hover:border-gray-500'}
      `}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only"
        />
        {checked && <Check size={12} className="text-white" />}
      </div>
      <span className={`text-sm transition-colors ${checked ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`}>
        {label}
      </span>
    </label>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-[#111] border-l border-white/10 z-50 overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/5">
              <h2 className="text-xl font-bold text-white">Filters</h2>
              <button 
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/5"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <FilterSection title="Location">
                <div className="space-y-1">
                  {LOCATION_OPTIONS.map((location) => (
                    <Checkbox
                      key={location}
                      checked={filters.location.includes(location)}
                      onChange={() => handleMultipleToggle('location', location)}
                      label={location}
                    />
                  ))}
                </div>
              </FilterSection>

              <FilterSection title="Space Type">
                <div className="space-y-1">
                  {SPACE_TYPE_OPTIONS.map((type) => (
                    <Checkbox
                      key={type}
                      checked={filters.spaceType.includes(type)}
                      onChange={() => handleMultipleToggle('spaceType', type)}
                      label={type}
                    />
                  ))}
                </div>
              </FilterSection>

              <FilterSection title="Noise Level">
                <div className="grid grid-cols-3 gap-3">
                  {NOISE_LEVELS.map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setFilters(prev => ({ 
                        ...prev, 
                        noiseLevel: value === prev.noiseLevel ? '' : value 
                      }))}
                      className={`
                        py-3 px-4 rounded-xl text-sm font-medium border transition-all duration-200
                        ${filters.noiseLevel === value 
                          ? 'bg-purple-600 border-purple-600 text-white' 
                          : 'bg-white/5 border-transparent text-gray-400 hover:bg-white/10'}
                      `}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </FilterSection>

              <FilterSection title="Amenities">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                  {FEATURE_OPTIONS.map(({ value, label }) => (
                    <Checkbox
                      key={value}
                      checked={filters.features.includes(value)}
                      onChange={() => handleMultipleToggle('features', value)}
                      label={label}
                    />
                  ))}
                </div>
              </FilterSection>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/5 bg-[#111]">
              <div className="flex gap-4">
                <button
                  onClick={() => setFilters({
                    location: [],
                    noiseLevel: '',
                    spaceType: [],
                    features: []
                  })}
                  className="px-6 py-3 rounded-xl border border-white/10 text-white font-medium hover:bg-white/5 transition-colors"
                >
                  Reset
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-3 rounded-xl bg-white text-black font-bold hover:bg-gray-200 transition-colors"
                >
                  Show Results
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FilterPanel;